import { FALLBACK_LOCALE, SUPPORTED_LOCALES, type Locale } from "../locales.js";

import de from "./locales/de.json" with { type: "json" };
import en from "./locales/en.json" with { type: "json" };
import es from "./locales/es.json" with { type: "json" };
import fr from "./locales/fr.json" with { type: "json" };
import nl from "./locales/nl.json" with { type: "json" };

/**
 * Catalogues de traduction de l'interface.
 *
 * Le français fait référence : c'est la langue de rédaction, et l'ensemble de ses
 * clés définit ce que les autres langues doivent couvrir. Une clé manquante ou
 * vide fait échouer l'intégration continue, de sorte qu'une langue incomplète
 * n'atteint jamais la production (INTERNATIONAL.md §3).
 */
export const MESSAGES: Record<Locale, Record<string, string>> = {
  fr,
  en,
  es,
  de,
  nl,
};

export type MessageKey = keyof typeof fr;

/** Écrans dont une formulation approximative a des conséquences réelles. */
export const CRITICAL_KEY_PREFIXES = ["safety."] as const;

export function isCriticalKey(key: string): boolean {
  return CRITICAL_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Traduit une clé, en se repliant sur la langue de rédaction si nécessaire.
 *
 * Les paramètres sont substitués sous la forme `{{nom}}`.
 */
export function translate(
  locale: Locale,
  key: MessageKey,
  params: Record<string, string | number> = {},
): string {
  const catalogue = MESSAGES[locale];
  const template = catalogue[key] || MESSAGES[FALLBACK_LOCALE][key] || key;

  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/** Écarts entre le catalogue de référence et celui d'une langue. */
export type CatalogueReport = {
  locale: Locale;
  missing: string[];
  empty: string[];
  extra: string[];
  placeholderMismatch: string[];
};

function placeholders(text: string): string[] {
  return [...text.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1] as string).sort();
}

/**
 * Compare chaque catalogue au français.
 *
 * Contrôle aussi les paramètres : une traduction qui perd un `{{distance}}`
 * compile parfaitement mais affiche une phrase amputée à l'exécution.
 */
export function auditCatalogues(): CatalogueReport[] {
  const referenceKeys = Object.keys(MESSAGES[FALLBACK_LOCALE]);

  return SUPPORTED_LOCALES.filter((locale) => locale !== FALLBACK_LOCALE).map(
    (locale) => {
      const catalogue = MESSAGES[locale];
      const keys = new Set(Object.keys(catalogue));

      const missing = referenceKeys.filter((key) => !keys.has(key));
      const empty = referenceKeys.filter(
        (key) => keys.has(key) && !catalogue[key]?.trim(),
      );
      const extra = [...keys].filter((key) => !referenceKeys.includes(key));
      const placeholderMismatch = referenceKeys.filter((key) => {
        const source = MESSAGES[FALLBACK_LOCALE][key];
        const target = catalogue[key];
        if (!source || !target) return false;
        return placeholders(source).join(",") !== placeholders(target).join(",");
      });

      return { locale, missing, empty, extra, placeholderMismatch };
    },
  );
}
